/**
 * Lista de kanbans — /kanbans (Fase 6).
 *
 * 3 zonas:
 *   - EntityHeader (titulo + acao "Novo kanban")
 *   - KpiStrip (total + por tipo)
 *   - Card com filtros (busca + tipo) + grid de cards (clique abre board)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiClipboard, FiFileText, FiGrid, FiPlus, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { unidadeDefault } from "../../services/unidades";
import {
  criarKanban,
  deletarKanban,
  listarKanbans,
} from "../../services/kanbans";
import { PageContainer } from "../../styles/shared";

import {
  BoardCard,
  BoardCardDescricao,
  BoardCardMeta,
  BoardCardTitle,
  BoardsGrid,
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  IconButton,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  Select,
  Textarea,
} from "./styles";

const TIPO_LABEL = {
  PEDIDO: "Pedidos",
  ORCAMENTO: "Orçamentos",
  LIVRE: "Livre",
};
const TIPO_TONE = {
  PEDIDO: "info",
  ORCAMENTO: "warning",
  LIVRE: "neutral",
};
const TIPO_ICONE = {
  PEDIDO: FiClipboard,
  ORCAMENTO: FiFileText,
  LIVRE: FiGrid,
};

function KanbansLista() {
  const navigate = useNavigate();
  const { request, isMaster, hasAllPermissions } = useAuth();

  const [unidade, setUnidade] = useState(null);
  const [kanbans, setKanbans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [busca, setBusca] = useState("");
  const [criando, setCriando] = useState(null); // null | {nome, tipo_entidade, descricao}

  const podeCriar = isMaster || hasAllPermissions(["kanban:create"]);
  const podeDeletar = isMaster || hasAllPermissions(["kanban:delete"]);

  useEffect(() => {
    unidadeDefault(request).then(setUnidade);
  }, [request]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 100 };
      if (filtroTipo) params.tipo = filtroTipo;
      if (busca) params.busca = busca;
      const payload = await listarKanbans(request, params);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setKanbans(items);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar kanbans.");
    } finally {
      setLoading(false);
    }
  }, [request, filtroTipo, busca]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const counts = useMemo(() => {
    const c = { total: kanbans.length, PEDIDO: 0, ORCAMENTO: 0, LIVRE: 0 };
    kanbans.forEach((k) => {
      c[k.tipo_entidade] = (c[k.tipo_entidade] || 0) + 1;
    });
    return c;
  }, [kanbans]);

  const kpis = [
    { label: "Total", value: counts.total },
    { label: "Pedidos", value: counts.PEDIDO, tone: "info" },
    { label: "Orçamentos", value: counts.ORCAMENTO, tone: "warning" },
    { label: "Livre", value: counts.LIVRE, tone: "neutral" },
  ];

  const handleAbrirCriacao = () => {
    setCriando({ nome: "", tipo_entidade: "LIVRE", descricao: "" });
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!criando?.nome.trim()) {
      setErro("Nome do kanban eh obrigatório.");
      return;
    }
    if (!unidade?.id) {
      setErro("Unidade não carregada.");
      return;
    }
    setErro(null);
    try {
      const novo = await criarKanban(request, {
        nome: criando.nome.trim(),
        tipo_entidade: criando.tipo_entidade,
        descricao: criando.descricao || "",
        business_unit: unidade.id,
      });
      setCriando(null);
      navigate(`/kanbans/${novo.id}`);
    } catch (err) {
      setErro(err?.message || "Erro ao criar kanban.");
    }
  };

  const handleDeletar = async (k, e) => {
    e.stopPropagation();
    if (!window.confirm(`Apagar kanban "${k.nome}" e seus cartoes?`)) return;
    try {
      await deletarKanban(request, k.id);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao apagar.");
    }
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Kanbans"
        subtitle={`${counts.total} board${counts.total !== 1 ? "s" : ""} configurado${counts.total !== 1 ? "s" : ""}`}
        primaryAction={
          podeCriar
            ? {
                label: "Novo kanban",
                icon: <FiPlus size={14} aria-hidden="true" />,
                onClick: handleAbrirCriacao,
              }
            : undefined
        }
        actions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <SearchInput
            type="search"
            placeholder="Buscar por nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="">Todos os tipos</option>
            <option value="PEDIDO">Pedidos</option>
            <option value="ORCAMENTO">Orçamentos</option>
            <option value="LIVRE">Livre</option>
          </Select>
        </div>

        {erro && (
          <ErrorMsg>
            <FiAlertCircle size={14} /> {erro}
          </ErrorMsg>
        )}

        {loading ? (
          <EmptyMsg>Carregando kanbans...</EmptyMsg>
        ) : kanbans.length === 0 ? (
          <EmptyMsg>
            Nenhum kanban configurado.
            {podeCriar && " Clique em \"Novo kanban\" para comecar."}
          </EmptyMsg>
        ) : (
          <BoardsGrid>
            {kanbans.map((k) => {
              const Icone = TIPO_ICONE[k.tipo_entidade] || FiGrid;
              return (
                <BoardCard key={k.id} onClick={() => navigate(`/kanbans/${k.id}`)}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Icone size={20} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <BoardCardTitle>{k.nome}</BoardCardTitle>
                      {k.descricao && <BoardCardDescricao>{k.descricao}</BoardCardDescricao>}
                    </div>
                  </div>
                  <BoardCardMeta>
                    <Pill $tone={TIPO_TONE[k.tipo_entidade]}>{TIPO_LABEL[k.tipo_entidade]}</Pill>
                    <span>{k.qtd_estagios} estagio{k.qtd_estagios !== 1 ? "s" : ""}</span>
                    <span>{k.qtd_cartoes} cartao{k.qtd_cartoes !== 1 ? "es" : ""}</span>
                  </BoardCardMeta>
                  {podeDeletar && (
                    <div style={{ marginTop: 4 }}>
                      <IconButton
                        type="button"
                        onClick={(e) => handleDeletar(k, e)}
                        title="Apagar kanban"
                      >
                        Apagar
                      </IconButton>
                    </div>
                  )}
                </BoardCard>
              );
            })}
          </BoardsGrid>
        )}
      </Card>

      {/* Modal: criar novo kanban */}
      <SlideOverPanel
        open={Boolean(criando)}
        onClose={() => setCriando(null)}
        title="Novo kanban"
        subtitle="Defina nome + tipo. Estagios default serao criados (editaveis depois)."
        width="min(90vw, 480px)"
      >
        {criando && (
          <Form onSubmit={handleSalvar}>
            <Field>
              Nome
              <Input
                type="text"
                value={criando.nome}
                onChange={(e) => setCriando((p) => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: Produção 2026, Sprint engenharia..."
                maxLength={120}
                required
                autoFocus
              />
            </Field>
            <Field>
              Tipo
              <Select
                value={criando.tipo_entidade}
                onChange={(e) => setCriando((p) => ({ ...p, tipo_entidade: e.target.value }))}
              >
                <option value="LIVRE">Livre (cards textuais soltos)</option>
                <option value="PEDIDO">Pedidos (cards = pedidos reais do ERP)</option>
                <option value="ORCAMENTO">Orçamentos (cards = orçamentos reais)</option>
              </Select>
              <InlineHint>
                {criando.tipo_entidade === "LIVRE"
                  ? "Você cria cartoes manualmente com texto livre."
                  : "Você conecta cartoes a entidades existentes do ERP."}
              </InlineHint>
            </Field>
            <Field>
              Descricao (opcional)
              <Textarea
                value={criando.descricao}
                onChange={(e) => setCriando((p) => ({ ...p, descricao: e.target.value }))}
                rows={2}
                maxLength={300}
                placeholder="Para que serve este kanban?"
              />
            </Field>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setCriando(null)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">Criar e abrir</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}

export default KanbansLista;
